import React from 'react';
import { AdminPageHeader } from './components/AdminPageHeader';
import { AdminStatCard } from './components/AdminStatCard';

// ============================================================================
// Types
// ============================================================================

export interface AdminAnalyticsPageProps {
  timeRange: number;
  startDate: string;
  endDate: string;

  // Stats
  totalVisits: number;
  uniqueVisitors: number;
  authenticatedVisits: number;
  totalEvents: number;

  // Data
  visitsByDay: Record<string, number>;
  activeUsersByDay: Record<string, number>;
  newUsersByDay: Record<string, number>;
  moodLoggersByDay: Record<string, number>;

  // Lists
  topPages: Array<[string, number]>;
  topLandingPages: Array<[string, number]>;
  topReferrers: Array<[string, number]>;
  topEvents: Array<[string, number]>;
  browsers: Array<[string, number]>;
  deviceTypes: Array<[string, number]>;
  operatingSystems: Array<[string, number]>;
  countries: Array<[string, number]>;

  // URLs
  dashboardPath: string;
  analyticsPath: string;

  // Labels
  labels: {
    title: string;
    subtitle: string;
    backLabel: string;
    // Time range
    last7Days: string;
    last30Days: string;
    last90Days: string;
    showingData: string;
    to: string;
    // Stats
    totalVisitsLabel: string;
    uniqueVisitorsLabel: string;
    authenticatedVisitsLabel: string;
    totalEventsLabel: string;
    // Sections
    uniqueVisitorsOverTimeTitle: string;
    topPagesTitle: string;
    topLandingPagesTitle: string;
    topReferrersTitle: string;
    activeSignedInUsersTitle: string;
    newUserRegistrationsTitle: string;
    usersWhoLoggedMoodTitle: string;
    topEventsTitle: string;
    browsersTitle: string;
    deviceTypesTitle: string;
    operatingSystemsTitle: string;
    topCountriesTitle: string;
    // Table
    dateColumn: string;
    countColumn: string;
    chartColumn: string;
    usersColumn: string;
    eventNameColumn: string;
    percentageColumn: string;
    // Empty states
    noVisitData: string;
    noPageViewData: string;
    noLandingPageData: string;
    noReferrerData: string;
    noActiveUserData: string;
    noRegistrationData: string;
    noMoodLoggingData: string;
    noEventData: string;
    noBrowserData: string;
    noDeviceData: string;
    noOSData: string;
    noCountryData: string;
  };
}

// ============================================================================
// Helper Components
// ============================================================================

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ProgressBarTable({
  data,
  dateKey = true,
  progressColor = '',
  emptyMessage,
  labels,
}: {
  data: Record<string, number>;
  dateKey?: boolean;
  progressColor?: string;
  emptyMessage: string;
  labels: AdminAnalyticsPageProps['labels'];
}) {
  const entries = Object.entries(data).sort();
  const maxValue = Math.max(...entries.map(([, v]) => v), 1);

  if (entries.length === 0) {
    return <p className="text-muted">{emptyMessage}</p>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm">
        <thead>
          <tr>
            <th>{labels.dateColumn}</th>
            <th>{labels.usersColumn}</th>
            <th>{labels.chartColumn}</th>
          </tr>
        </thead>
        <tbody>
          {entries.reverse().map(([key, value]) => (
            <tr key={key}>
              <td>{dateKey ? formatDate(key) : key}</td>
              <td>
                <strong>{value}</strong>
              </td>
              <td>
                <div className="progress" style={{ height: 20 }}>
                  <div
                    className={`progress-bar ${progressColor}`}
                    role="progressbar"
                    style={{ width: `${(value / maxValue) * 100}%` }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ListWithBadges({
  data,
  badgeColor,
  emptyMessage,
  maxItems = 5,
  extractHost = false,
}: {
  data: Array<[string, number]>;
  badgeColor: string;
  emptyMessage: string;
  maxItems?: number;
  extractHost?: boolean;
}) {
  if (data.length === 0) {
    return <p className="text-muted">{emptyMessage}</p>;
  }

  const displayData = data.slice(0, maxItems);

  const getDisplayValue = (value: string) => {
    if (extractHost) {
      try {
        return new URL(value).host;
      } catch {
        return value;
      }
    }
    return value || 'Unknown';
  };

  return (
    <div className="list-group list-group-flush">
      {displayData.map(([name, count], index) => (
        <div
          key={index}
          className="list-group-item px-0 d-flex justify-content-between align-items-center"
        >
          <span className="text-truncate" style={{ maxWidth: '70%' }} title={name}>
            {getDisplayValue(name)}
          </span>
          <span className={`badge ${badgeColor} rounded-pill`}>{count}</span>
        </div>
      ))}
    </div>
  );
}

function TopEventsTable({
  events,
  emptyMessage,
  labels,
}: {
  events: Array<[string, number]>;
  emptyMessage: string;
  labels: AdminAnalyticsPageProps['labels'];
}) {
  if (events.length === 0) {
    return <p className="text-muted">{emptyMessage}</p>;
  }

  const totalCount = events.reduce((sum, [, count]) => sum + count, 0);

  return (
    <div className="table-responsive">
      <table className="table table-hover">
        <thead>
          <tr>
            <th>{labels.eventNameColumn}</th>
            <th>{labels.countColumn}</th>
            <th>{labels.percentageColumn}</th>
          </tr>
        </thead>
        <tbody>
          {events.map(([name, count], index) => {
            const percentage = (count / totalCount) * 100;
            return (
              <tr key={index}>
                <td>
                  <code>{name}</code>
                </td>
                <td>
                  <strong>{count.toLocaleString()}</strong>
                </td>
                <td>
                  <div className="progress" style={{ height: 20, width: 100 }}>
                    <div
                      className="progress-bar bg-warning"
                      role="progressbar"
                      style={{ width: `${percentage}%` }}
                    >
                      {Math.round(percentage)}%
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminAnalyticsPage({
  timeRange,
  startDate,
  endDate,
  totalVisits,
  uniqueVisitors,
  authenticatedVisits,
  totalEvents,
  visitsByDay,
  activeUsersByDay,
  newUsersByDay,
  moodLoggersByDay,
  topPages,
  topLandingPages,
  topReferrers,
  topEvents,
  browsers,
  deviceTypes,
  operatingSystems,
  countries,
  dashboardPath,
  analyticsPath,
  labels,
}: AdminAnalyticsPageProps) {
  const authenticatedPercent =
    totalVisits > 0 ? ((authenticatedVisits / totalVisits) * 100).toFixed(1) : '0';

  const buildTimeRangeUrl = (days: number) => `${analyticsPath}?time_range=${days}`;

  return (
    <div>
      <AdminPageHeader
        title={labels.title}
        subtitle={labels.subtitle}
        icon="graph-up"
        backLink={{ href: dashboardPath, label: labels.backLabel }}
      />

      {/* Time Range Filter */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="btn-group" role="group">
            <a
              href={buildTimeRangeUrl(7)}
              className={`btn btn-sm ${timeRange === 7 ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              {labels.last7Days}
            </a>
            <a
              href={buildTimeRangeUrl(30)}
              className={`btn btn-sm ${timeRange === 30 ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              {labels.last30Days}
            </a>
            <a
              href={buildTimeRangeUrl(90)}
              className={`btn btn-sm ${timeRange === 90 ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              {labels.last90Days}
            </a>
          </div>
          <span className="ms-3 text-muted">
            {labels.showingData} {formatDate(startDate)} {labels.to} {formatDate(endDate)}
          </span>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="row g-3 mb-4">
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.totalVisitsLabel}
            value={totalVisits}
            icon="eye"
            iconColor="primary"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.uniqueVisitorsLabel}
            value={uniqueVisitors}
            icon="person-check"
            iconColor="success"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.authenticatedVisitsLabel}
            value={authenticatedVisits}
            subtitle={`${authenticatedPercent}%`}
            icon="shield-check"
            iconColor="info"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.totalEventsLabel}
            value={totalEvents}
            icon="lightning"
            iconColor="warning"
          />
        </div>
      </div>

      {/* Visits Chart */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h5 className="mb-0">
            <i className="bi bi-graph-up me-2" />
            {labels.uniqueVisitorsOverTimeTitle}
          </h5>
        </div>
        <div className="card-body">
          <ProgressBarTable
            data={visitsByDay}
            emptyMessage={labels.noVisitData}
            labels={labels}
          />
        </div>
      </div>

      {/* Top Pages and Landing Pages */}
      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-file-text me-2" />
                {labels.topPagesTitle}
              </h5>
            </div>
            <div className="card-body">
              <ListWithBadges
                data={topPages}
                badgeColor="bg-primary"
                emptyMessage={labels.noPageViewData}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-door-open me-2" />
                {labels.topLandingPagesTitle}
              </h5>
            </div>
            <div className="card-body">
              <ListWithBadges
                data={topLandingPages}
                badgeColor="bg-success"
                emptyMessage={labels.noLandingPageData}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Traffic Sources and User Activity */}
      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-link-45deg me-2" />
                {labels.topReferrersTitle}
              </h5>
            </div>
            <div className="card-body">
              <ListWithBadges
                data={topReferrers}
                badgeColor="bg-info"
                emptyMessage={labels.noReferrerData}
                extractHost
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-activity me-2" />
                {labels.activeSignedInUsersTitle}
              </h5>
            </div>
            <div className="card-body">
              <ProgressBarTable
                data={activeUsersByDay}
                progressColor="bg-success"
                emptyMessage={labels.noActiveUserData}
                labels={labels}
              />
            </div>
          </div>
        </div>
      </div>

      {/* New User Registrations and Mood Logging */}
      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-person-plus me-2" />
                {labels.newUserRegistrationsTitle}
              </h5>
            </div>
            <div className="card-body">
              <ProgressBarTable
                data={newUsersByDay}
                progressColor="bg-primary"
                emptyMessage={labels.noRegistrationData}
                labels={labels}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-emoji-smile me-2" />
                {labels.usersWhoLoggedMoodTitle}
              </h5>
            </div>
            <div className="card-body">
              <ProgressBarTable
                data={moodLoggersByDay}
                progressColor="bg-warning"
                emptyMessage={labels.noMoodLoggingData}
                labels={labels}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Top Events */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h5 className="mb-0">
            <i className="bi bi-lightning-charge me-2" />
            {labels.topEventsTitle}
          </h5>
        </div>
        <div className="card-body">
          <TopEventsTable events={topEvents} emptyMessage={labels.noEventData} labels={labels} />
        </div>
      </div>

      {/* Technology Stats */}
      <div className="row g-3 mb-4">
        <div className="col-lg-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-browser-chrome me-2" />
                {labels.browsersTitle}
              </h5>
            </div>
            <div className="card-body">
              <ListWithBadges
                data={browsers}
                badgeColor="bg-secondary"
                emptyMessage={labels.noBrowserData}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-phone me-2" />
                {labels.deviceTypesTitle}
              </h5>
            </div>
            <div className="card-body">
              <ListWithBadges
                data={deviceTypes}
                badgeColor="bg-secondary"
                emptyMessage={labels.noDeviceData}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-laptop me-2" />
                {labels.operatingSystemsTitle}
              </h5>
            </div>
            <div className="card-body">
              <ListWithBadges
                data={operatingSystems}
                badgeColor="bg-secondary"
                emptyMessage={labels.noOSData}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Countries */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h5 className="mb-0">
            <i className="bi bi-globe me-2" />
            {labels.topCountriesTitle}
          </h5>
        </div>
        <div className="card-body">
          {countries.length > 0 ? (
            <div className="row">
              {countries.map(([country, count], index) => (
                <div key={index} className="col-md-6 col-lg-4 mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{country}</span>
                    <span className="badge bg-primary rounded-pill">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted">{labels.noCountryData}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminAnalyticsPage;
